import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Send, Hammer, Users, DollarSign } from "lucide-react";
import { submitPortfolioContact } from "@/lib/api";
import { E } from "@/components/EditableText";

const SUN = "#F5B400";
const INK = "#0F1E2E";
const CREAM = "#FDFBF5";

const TRACKED_TOTAL = 6;

function Field({ label, htmlFor, children }) {
  return (
    <label htmlFor={htmlFor} className="block">
      <div className="pcp-mono text-[10px] opacity-70 mb-1">{label}</div>
      {children}
    </label>
  );
}

const inputCls = "w-full bg-transparent border-b border-cream/30 focus:border-cream py-2 outline-none text-cream placeholder:text-cream/40";
const areaCls = "w-full bg-transparent border border-cream/20 focus:border-cream/60 rounded-md p-3 outline-none text-cream placeholder:text-cream/40 text-sm resize-y";

export function CoreContact({ prospect, engagementPct, seenSections }) {
  const [form, setForm] = useState({
    name: "", email: "", organization: prospect?.company_name || "", role: "",
    suggestedBuild: "", suggestedTeam: "", suggestedPricing: "", notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.organization.trim()) {
      toast.error("Name, email, and company are required.");
      return;
    }
    setBusy(true);
    try {
      const res = await submitPortfolioContact({
        name: form.name.trim(),
        email: form.email.trim(),
        organization: form.organization.trim(),
        role: form.role.trim() || null,
        archetype: "other",
        notes: form.notes.trim() || null,
        suggested_build: form.suggestedBuild.trim() || null,
        suggested_team: form.suggestedTeam.trim() || null,
        suggested_pricing: form.suggestedPricing.trim() || null,
        engagement_pct: engagementPct,
        sections_viewed: seenSections,
        source: "core-deck",
        prospect_slug: prospect?.slug || null,
      });
      if (res?.ok) {
        setDone(true);
        toast.success("Sent — Colin will reach out shortly.");
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } catch (err) {
      toast.error("Network error — please email colindarke@complisun.com directly.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="contact" className="px-6 md:px-12 py-16 md:py-24" style={{ background: INK, color: CREAM }}>
      <div className="max-w-6xl mx-auto grid lg:grid-cols-[1fr_1fr] gap-12">
        <div>
          <p className="pcp-mono text-[11px] mb-4" style={{ color: SUN }}>/ Send your ideal engagement</p>
          <h2 className="pcp-h text-4xl md:text-5xl mb-6 leading-[1.05]">
            <E k="contact.headline" defaultText="You've designed it. Now send it to Colin." as="span" multiline />
          </h2>
          <p className="text-base md:text-lg opacity-80 leading-relaxed mb-8">
            <E
              k="contact.body"
              defaultText="Tell us what your ideal engagement looks like — the modules, the people, even the price. Suggest a build we should ship for you first, name the team members you want, propose the pricing that works for your budget. We'll come back with a concrete program design. No spam, no drip — direct from Colin Darke."
              as="span"
              multiline
            />
          </p>
          <ul className="space-y-3 text-sm opacity-80">
            {[
              "Detroit-rooted, attorney-led — we answer the phone.",
              "You design it; we operationalize it.",
              "Immediate automation. Then iteration and improvement together.",
              "If we're not a fit, we'll say so and refer where it makes sense.",
            ].map((t) => (
              <li key={t} className="flex items-start gap-3">
                <CheckCircle2 className="h-4 w-4 mt-1 flex-shrink-0" style={{ color: SUN }} strokeWidth={2} />
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </div>

        <div>
          {done ? (
            <div className="rounded-xl p-8 text-center" style={{ background: "rgba(255,255,255,0.04)", border: `1px solid ${SUN}` }} data-testid="core-contact-done">
              <CheckCircle2 className="h-12 w-12 mx-auto mb-4" style={{ color: SUN }} strokeWidth={1.6} />
              <h3 className="pcp-h text-3xl mb-3">Got it.</h3>
              <p className="opacity-80 mb-4">Colin received your ideal engagement. Expect a reply within one business day.</p>
              <p className="pcp-mono text-[10px] opacity-60">Sections viewed: {seenSections.length}/{TRACKED_TOTAL} · Engagement {engagementPct}%</p>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="rounded-xl p-6 md:p-8" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.10)" }} data-testid="core-contact-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Name" htmlFor="core-name">
                  <input id="core-name" data-testid="core-contact-name" value={form.name} onChange={set("name")} required className={inputCls} />
                </Field>
                <Field label="Email" htmlFor="core-email">
                  <input id="core-email" type="email" data-testid="core-contact-email" value={form.email} onChange={set("email")} required className={inputCls} />
                </Field>
                <Field label="Company" htmlFor="core-org">
                  <input id="core-org" data-testid="core-contact-org" value={form.organization} onChange={set("organization")} required className={inputCls} />
                </Field>
                <Field label="Role (optional)" htmlFor="core-role">
                  <input id="core-role" data-testid="core-contact-role" value={form.role} onChange={set("role")} placeholder="CCO · GC · Founder" className={inputCls} />
                </Field>
              </div>

              <div className="mt-6 space-y-4">
                <div>
                  <div className="pcp-mono text-[10px] opacity-70 mb-2 flex items-center gap-1.5">
                    <Hammer className="h-3 w-3" style={{ color: SUN }} /> / Suggest a build (optional)
                  </div>
                  <textarea data-testid="core-suggest-build" value={form.suggestedBuild} onChange={set("suggestedBuild")} rows={2}
                            placeholder="What should we build for you first? A module, a workflow, a report your board keeps asking for…"
                            className={areaCls} />
                </div>
                <div>
                  <div className="pcp-mono text-[10px] opacity-70 mb-2 flex items-center gap-1.5">
                    <Users className="h-3 w-3" style={{ color: SUN }} /> / Suggest team members (optional)
                  </div>
                  <textarea data-testid="core-suggest-team" value={form.suggestedTeam} onChange={set("suggestedTeam")} rows={2}
                            placeholder="Who from the roster do you want on your engagement, and roughly how many hours?"
                            className={areaCls} />
                </div>
                <div>
                  <div className="pcp-mono text-[10px] opacity-70 mb-2 flex items-center gap-1.5">
                    <DollarSign className="h-3 w-3" style={{ color: SUN }} /> / Suggest pricing (optional)
                  </div>
                  <textarea data-testid="core-suggest-pricing" value={form.suggestedPricing} onChange={set("suggestedPricing")} rows={2}
                            placeholder="Tell us the structure or number that works for your budget — we'd rather hear it than guess."
                            className={areaCls} />
                </div>
                <div>
                  <div className="pcp-mono text-[10px] opacity-70 mb-2">/ Anything else (optional)</div>
                  <textarea data-testid="core-contact-notes" value={form.notes} onChange={set("notes")} rows={3}
                            placeholder="Bank partners, examiner pressure, states you operate in, timing…"
                            className={areaCls} />
                </div>
              </div>

              <button
                type="submit"
                disabled={busy}
                data-testid="core-contact-submit"
                className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-md font-medium transition disabled:opacity-50"
                style={{ background: SUN, color: INK }}
              >
                {busy ? (<><Loader2 className="h-4 w-4 animate-spin" /> Sending…</>) : (<><Send className="h-4 w-4" /> Send my ideal engagement to Colin</>)}
              </button>
              <p className="text-[11px] opacity-55 mt-3 text-center">
                Or email <a href="mailto:colindarke@complisun.com" className="underline">colindarke@complisun.com</a> directly.
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
